import { FlexSpinner } from 'components'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  MailTemplateForm,
  MailTemplateFormSchema,
  MailTemplateFormValues,
} from 'forms/MailTemplateForm'
import { MailTemplate, MailTemplateType } from 'gql.generated'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import useSelection from 'hooks/useSelection'
import { PanelType } from 'office-ui-fabric-react'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import { AdminMailTemplatesList } from './AdminMailTemplatesList'
import {
  CreateMailTemplateMutationVariables,
  DeleteMailTemplatesMutationVariables,
  UpdateMailTemplateMutationVariables,
  useCreateMailTemplateMutation,
  useDeleteMailTemplatesMutation,
  useGetMailTemplatesQuery,
  useUpdateMailTemplateMutation,
} from './AdminMailTemplatesView.generated'
import { RouteParamsMailTemplates } from './index'

export interface AdminMailTemplatesListViewProps {
  routeParams: RouteParamsMailTemplates
  updateRouteParams: (updateParams: Partial<RouteParamsMailTemplates>) => void
  filterState: string
  setFilterState: React.Dispatch<React.SetStateAction<string>>
  displayProvingItems: boolean
  setDisplayProvingItems: React.Dispatch<React.SetStateAction<boolean>>
}

export const AdminMailTemplatesListView: React.FunctionComponent<
  AdminMailTemplatesListViewProps
> = ({
  routeParams: { pgid, id, search },
  updateRouteParams,
  filterState,
  setFilterState,
  displayProvingItems,
  setDisplayProvingItems,
}) => {
  const { t } = useTranslation()

  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()

  const [createMailTemplate] = useCreateMailTemplateMutation()
  const [updateMailTemplate] = useUpdateMailTemplateMutation()
  const [deleteMailTemplates] = useDeleteMailTemplatesMutation()

  const { loading, error, refetch, data } = useGetMailTemplatesQuery({
    variables: {
      pgid,
    },
  })
  const items =
    data && data.mailTemplates
      ? data.mailTemplates.sort((m1, m2) => m1.name.localeCompare(m2.name))
      : []

  const [selection, selectedItems] = useSelection<MailTemplate>({
    getKey: (item) => item._id!,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  const handleSubmit = async (
    values: MailTemplateFormValues,
    formikActions: FormikActions<MailTemplateFormValues>
  ) => {
    formikActions.setSubmitting(true)

    if (!values.inactive) {
      values.inactive = false
    }

    if (values.provingGroundId === '') {
      values.provingGroundId = null
    }
    if (values.type === MailTemplateType.TaxRequirements) {
      values.to = undefined
      values.cc = undefined
    }

    if (formType === FormType.insert) {
      await createMailTemplate({
        variables: values as CreateMailTemplateMutationVariables,
      })
    } else if (formType === FormType.update) {
      await updateMailTemplate({
        variables: {
          ...values,
          id: selectedItemId,
        } as UpdateMailTemplateMutationVariables,
      })
    } else {
      throw new Error(
        ' Custom: Error while create or update MailTemplate. FormType not set.'
      )
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch()
    hidePanel()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteMailTemplates({
        variables: { ids } as DeleteMailTemplatesMutationVariables,
      })
    }
    refetch()
    hideDialog()
  }

  /** commands */
  useCreateCommand(
    'mailTemplate',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({
          type: MailTemplateType.General,
        })
      },
    },
    [setFormType]
  )
  useEditCommand(
    { name: 'mailTemplate', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(omitDeep(selectedItems[0], ['_id', '__typename']))
      },
    },
    [selectedItems, setFormType]
  )
  // useDeleteCommand(
  //   { name: 'mailTemplate', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <AdminMailTemplatesList
        items={items}
        selection={selection}
        onItemInvoked={() =>
          selectedItems.length === 1
            ? (() => {
                setFormType(FormType.update)
                setSelectedItemId(selectedItems[0]._id)
                showPanel(omitDeep(selectedItems[0], ['id', '__typename']))
              })()
            : false
        }
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new MailTemplateFormSchema()}
        type={PanelType.medium}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.mailTemplate.edit')
            : t('model.mailTemplate.create')
        }
      >
        <MailTemplateForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}
